import { Redo2, SquareMenu, Undo2, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AnchoredStylingBar } from './anchored-styling-bar';
import { useAnswerRefHtml } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { BlockCard, BlockPanelCard } from './block-card';
import { IssueBar, type IssueContext, IssuesPanel, useIssueContext , type BlockIssue } from './block-issues';
import { BlockNameField } from './block-name-field';
import { htmlHasContent } from './formula-plaintext';
import { remapInkColors } from './ink-colors';
import { PhoneModeToggle } from './input-block-frame';
import { processReactionHTML } from './reaction-formula-extension';
import { RichTextEditor } from './rich-text-editor';
import { DisplayBankRow, DisplayScenarioBar, useDisplayVersions } from './use-display-versions';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { PHONE_QUERY } from './use-viewport';
import { BLOCK_TEXT_CLASS, hasRollableVariables, htmlHasVariableChips, PreviewHtml, processVariableChipHTML, renderChipsForDisplay, rollVariableValues, useChipScope } from './variable-chip-preview';

import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block } from '@/types/experience';

import { usePhoneUndo } from '@/contexts/phone-undo-context';
import { useTaskPreviewRoll, useTaskVariables } from '@/contexts/task-variables-context';
import { isNameTaken } from '@/lib/block-naming';
import { sanitizeAuthoredHtml } from '@/lib/sanitize-authored-html';

function useIsMobile() {
  const query = PHONE_QUERY;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
    // `query` belongs here: a changed query means a different subscription, and the cleanup
    // above already tears the old one down.
  }, [query]);
  return isMobile;
}

/** The only field a Text variant carries. Named once so `applyDisplayVersion` and
 *  `writeDisplayVersion` cannot be given different lists. */
const TEXT_VERSION_KEYS = ['html'] as const;

interface TextBlockProps {
  block: Block;
  isPreview: boolean;
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

/**
 * THE ONE THING A TEXT BLOCK CAN GET WRONG: it is empty.
 *
 * Takes the HTML rather than a data object, because that is all there is to check — the block has
 * no answer, no feedback and no versions that change the question. `htmlHasContent`, not an inline
 * tag-strip: a block holding nothing but a math formula or a variable chip has no ordinary text
 * nodes and was once flagged empty when it was not.
 */
export function textIssues(html: string, ctx: IssueContext): BlockIssue[] {
  /* Destructured, like every other block's — the inventory generator reads `category: t('…')`
     off the source to resolve an issue's area, and `ctx.t(…)` is a shape it cannot follow. */
  const { t } = ctx;
  return htmlHasContent(html) ? [] : [{
    key: 'text-empty', severity: 'error', kind: 'incompletion',
    category: t('blocks.text.title'), detail: t('blocks.text.empty'),
  }];
}

/** From RAW stored data — the entry point the dispatcher calls. See `sortBlockIssues` for why
 *  each block owns this rather than the dispatcher knowing sixteen recipes. */
export function textBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return textIssues(((block.data as { html?: string } | undefined)?.html) ?? '', ctx);
}

export default function TextBlock({ block, isPreview, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, blocks, onRename }: TextBlockProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const phoneUndo = usePhoneUndo();
  const [rolledValues, setRolledValues] = useState<Record<string, string>>({});

  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !(block.data.html as string)));
  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);

  function handleLinkClick(e: React.MouseEvent) {
    const anchor = (e.target as HTMLElement).closest('a');
    if (anchor?.href) {
      e.preventDefault();
      e.stopPropagation();
      window.open(anchor.href, '_blank', 'noopener,noreferrer');
    }
  }

  function handleDone() {
    if (embedded) { onClose?.(); return; }
    setIsEditing(false);
    onClose?.();
  }

  /*
   * A TEXT BANK — several variants of what this block shows, one drawn per student.
   *
   * See `docs/linked-scenarios.md`. Text is the first DISPLAY block to get one, because a shared
   * scenario has to live somewhere that is not a question. `html` is the only thing that varies;
   * everything else about the block is shared, which for Text is nothing else at all.
   */
  const toolbar = useSharedEditorToolbar();
  const taskPreviewRoll = useTaskPreviewRoll();
  /**
   * EDIT OR TEST, on a phone.
   *
   * On a desktop the card and the editor are two instances of this component side by side, so
   * the card IS the test surface and nothing needs switching. A phone shows one full-screen
   * sheet, so without this a display block had no way to reach its own test state at all —
   * the input blocks have had the toggle since they got one, and the displays never did.
   */
  const [phoneMode, setPhoneMode] = useState<'edit' | 'test'>('edit');
  /* The bank, the three-way "which variant" rule and the write-the-whole-object guard all live
     in `useDisplayVersions` now, shared with Image, Sound and DataViz. */
  const {
    bank, leadsGroup, shown, testShown, shownVersionId, setTestVersion,
    editorVersionId, setEditorVersion, writeFields, addVariant, removeVariant, renameVariant, removeWarning,
  } = useDisplayVersions(block, TEXT_VERSION_KEYS, onUpdate, {
    isPreview, isEditing, taskPreviewRoll, blocks,
    nameFor: index => t('blocks.versions.scenarioDefault', { index }),
    warnFor: (names: string[]) => t('blocks.scenario.removeWarning', { names: names.join(', ') }),
  });
  const html = (shown.html as string) || '';
  /* The phone test pane reads the TEST pointer even while `isEditing`, for the same reason the
     desktop card does: stepping the test must not move the author's text under them. */
  const testHtml = (testShown.html as string) || '';

  /**
   * Every write sends the WHOLE data object.
   *
   * `onUpdate` replaces `block.data` wholesale — `handleUpdateBlock` does `{ ...b, data }` — so
   * the previous `onUpdate({ html })` was only ever safe because `html` was all this block had.
   * With a bank, that call would have deleted it on the first keystroke. Same delete-by-omission
   * that cost a question bank once already; `versionStructure` exists for the other road to it.
   */
  const writeHtml = (nextHtml: string) => writeFields({ html: nextHtml });
  /**
   * A text block with nothing in it.
   *
   * The block's first check. An ABSENCE, so `incompletion`: it stays quiet while the teacher is
   * writing and surfaces once they move on — a block created a moment ago is not a problem, one
   * left empty is. `severity: 'error'` because an empty text block contributes nothing to the task
   * and a student sees a blank space where an explanation should be.
   *
   * Tags are stripped before testing: an editor that has been focused and left can hold `<p></p>`
   * or a lone `<br>`, which is empty to a reader and non-empty to `length`.
   */
  /* `htmlHasContent`, not an inline tag-strip: this used to be its own regex, and a text block
     holding nothing but a math formula or a variable chip has no ordinary text nodes, so it was
     flagged empty when it was not. One test, shared with the scenario section. */
  const issues: BlockIssue[] = textIssues(html, issueCtx);
  const hasChips = htmlHasVariableChips(html);
  const hasRollable = hasRollableVariables(variables);
  /**
   * DISPLAY STATE WHEN CLOSED, ACTIVE STATE WHEN OPEN — the convention every input block
   * already follows, and the one this block had been missing since June.
   *
   * It used to need an eye button: the card showed chips, and a teacher who wanted to see what
   * a student would get had to turn on a private `previewMode` first. That predates the rule
   * (`project_block_active_vs_display_state_convention`) and contradicted it — an input block
   * does not ask, it rolls the moment you select it, because selecting a block IS saying "show
   * me this one". Being selected is the trigger, so the button had nothing left to do.
   */
  /*
   * Answer-reference chips resolve FIRST, and on every path.
   *
   * `useAnswerRefHtml` is what redraws a chip from its alias — the live name and the live
   * status — so the card and the editor show the same thing because they are reading the same
   * source, not because two renderers are kept in step. Without it the card renders whatever
   * name was baked into the stored html, which goes stale the moment an alias is renamed.
   *
   * It is a no-op on html with no chips in it, so the common case costs one `includes`.
   */
  /*
   * AND IT RESOLVES THEM UNDER TEST, which needs the roll handed in.
   *
   * A reference reads three ways — a chip while authoring, a value under test, the student's
   * own answer in a run — and the roll is the signal, exactly as it is for a variable chip.
   * Called with nothing, this surface was permanently the first of the three, so a reference
   * written into prose could never be read as a sentence. Prose is where a reference is most
   * naturally written, so that was most of them.
   *
   * A display block has no answer of its own, so "under test" is the card being SELECTED or
   * the phone's Test tab — the same two states the renders below already switch on.
   */
  const answerRefHtml = useAnswerRefHtml(isSelected || phoneMode === 'test' ? rolledValues : null);
  const withAnswerRefs = answerRefHtml(html);
  const chipScope = useChipScope();
  const displayHtml = isSelected
    ? processVariableChipHTML(withAnswerRefs, rolledValues, chipScope)
    : renderChipsForDisplay(withAnswerRefs, chipScope);


  /**
   * THE BANK — under the styling bar, above the text, in the heading every other block uses.
   *
   * `EditorSectionHeading` is the one the input blocks put their pager in, and it brings the
   * rule and the spacing with it: a hand-rolled row here was a copy of its label classes plus
   * guesses at the padding, which is the fork the house rule exists to stop.
   *
   * THE LABEL IS LOAD-BEARING. A bare `+` reads as a stray control splitting the styling buttons
   * from the text box; a labelled section reads as furniture. "Content" is the display blocks'
   * answer to the input blocks' QUESTION, and serves image, sound and dataviz unchanged when
   * they get banks.
   *
   * Once a bank exists the label becomes the NAMER — the same move the Decimals/Sig figs switch
   * makes, where the label was already saying what the control is about. A variant always HAS a
   * name to show (see `scenarioDefault`), so the swap never leaves the slot empty.
   */
  const bankRow = (
    <DisplayBankRow
      bank={bank}
      editorVersionId={editorVersionId}
      setEditorVersion={setEditorVersion}
      addVariant={addVariant}
      removeVariant={removeVariant}
      renameVariant={renameVariant}
      removeWarning={removeWarning}
    />
  );

  /** Another student's draw: a fresh variant AND fresh variable values, since a student meets
   *  both at once and testing one without the other is not what they will see. */
  /**
   * ROLLS THE NUMBERS, NEVER THE SCENARIO.
   *
   * It used to redraw the variant too. The pager already steps scenarios and the dice already
   * rolls variables, so doing both from one control left no way to ask for "this scenario,
   * different numbers" — the thing checking an answer key across rolls actually needs. Only full
   * Preview draws a scenario at random, because that is the surface standing in for a student.
   */
  function rerollVariables() {
    setRolledValues(rollVariableValues(variables));
  }

  /* One draw on open. Not on every render and not on every variable change — a roll that moved
     under the teacher while they read it would make the card unreadable rather than live. */
  useEffect(() => {
    if (isSelected) setRolledValues(rollVariableValues(variables));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  /**
   * THE CARD'S TEST BAR — the display block's answer to the input blocks' one.
   *
   * An input block's card carries a bar with Reroll and a "Version 1 of 2" pager, because the
   * card IS the live-test surface. A banked display block needs the same thing for the same
   * reason, and before this it had only a pager tucked in the card's top corner, which read as
   * card furniture rather than as testing.
   *
   * ONLY WHILE SELECTED, like every other test bar in the editor. A collapsed card is a
   * statement of what the block is, not a place to operate it, and a chooser sitting on one
   * invites paging a block you have not opened.
   *
   * What it does NOT take from that bar is Continuous, Submit and Reset: all three belong to
   * answering a question, and a display block has none. What is left is the two controls that
   * do apply, and with only two there is no reason to stack them — one row, where the input
   * blocks need two.
   *
   * REROLL IS A REAL DRAW, uniform over the bank, so a two-variant block will show you the same
   * one about half the time. That is not a bug to fix with "pick a different one": rerolling is
   * the realistic pass and stepping is the deliberate one, which is exactly why both are here.
   * `VersionBranchNotice` makes the same argument for the input blocks' pager.
   */
  /** Is there anything to test? A bank to page, or a variable to roll. */
  const hasTestState = bank.length > 1 || (hasChips && hasRollable);
  const scenarioBar = (
    <DisplayScenarioBar bank={bank} activeId={shownVersionId} onSelect={setTestVersion} onReroll={hasChips && hasRollable ? rerollVariables : undefined} />
  );
  const cardTestBar = (isSelected || leadsGroup) && !isPreview && !isEditing && hasTestState ? scenarioBar : null;

  if (isPreview) {
    return html ? (
      /*
       * THROUGH `PreviewHtml`, like every other student-facing surface.
       *
       * This branch used to write the stored HTML straight into the DOM, which left a variable
       * chip exactly as tiptap stored it: `<span data-variable-chip="riv">`. Unresolved, so a
       * student read the variable's NAME where its value belonged — and where the span had no
       * text of its own, nothing at all. The one surface that has to show real numbers was the
       * one surface not resolving them.
       *
       * `PreviewHtml` reads the task-level `BlockPreviewProvider`, which Preview already wraps
       * the page in, so the chip resolves against the same roll every other block reads. It
       * also applies `remapInkColors`, which is why that call is gone from here rather than
       * moved — it was the previous reason this block was special, and it no longer is.
       *
       * The wrapper carries the link handler, which belongs to the block and not to the HTML.
       */
      <div onClick={handleLinkClick}>
        <PreviewHtml html={html} className={BLOCK_TEXT_CLASS} />
      </div>
    ) : null;
  }

  if (!isEditing) {
    /*
     * NOT A BUTTON WHILE SELECTED, which is the rule `CollapsedInputBlock` already states for
     * its own content: a selected card is the live test surface, its children handle their
     * own clicks and keyboard, and wrapping `role="button"` round real controls is something
     * no screen reader can make sense of. This card had the role unconditionally, so a
     * reference inside its prose could not be a real button and the click it swallowed had to
     * be caught in the capture phase.
     */
    const contentProps = isSelected ? {} : {
      tabIndex: 0 as const,
      role: 'button' as const,
      /*
       * FROM THE RESOLVED HTML, not the stored html.
       *
       * The stored copy carries each chip's BAKED text, which is a snapshot: it goes stale on
       * every rename, and chips written before the insertion bug was fixed baked the words
       * "Missing block", so this card announced itself as "Edit text block: Missing block
       * Missing block Missing block". `withAnswerRefs` has each chip redrawn from its live
       * alias, which is what the card shows and therefore what it should say.
       */
      'aria-label': html ? t('blocks.text.editAriaWithContent', { content: withAnswerRefs.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) }) : t('blocks.text.editAriaEmpty'),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isDeleteArmed) { disarmDelete(); return; }
          if (onSelect) onSelect(); else setIsEditing(true);
        }
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.detail === 0) return;
        if (isDeleteArmed) { disarmDelete(); return; }
        if (onSelect) onSelect(); else setIsEditing(true);
      },
    };
    const contentChildren = (
      <>
        {cardTestBar}
        {htmlHasContent(html) ? (
          /* Reactions REDRAWN, not replayed — see `processReactionHTML`. The card and Preview
             are two renders of the same stored HTML and they disagreed about the co-substrate
             arc, because that HTML is baked at save and only one of the two re-derived it. */
          <div onClick={handleLinkClick} className={BLOCK_TEXT_CLASS} dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(processReactionHTML(remapInkColors(displayHtml))) }} />
        ) : (
          <p className='text-sm italic text-ws-muted'>{t('blocks.text.empty')}</p>
        )}
      </>
    );

    if (isMain) {
      return (
        <BlockCard
          block={block}
          cardRef={blockRef}
          icon={<SquareMenu className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
          isDeleteArmed={isDeleteArmed}
          isSelected={isSelected}
          isDragOver={isDragOver}
          onDuplicate={triggerDuplicate}
          onDelete={triggerDelete}
          onDisarm={disarmDelete}
        >
          {/* BlockCard is a flex ROW and each child is a COLUMN of it, so the content and the
              issue bar have to arrive as ONE child. Passed as two siblings, the bar became a
              second column and sat down the right-hand side of the card instead of under the
              text. Image, Sound and DataViz all wrap theirs this way already; Text was the one
              that did not. */}
          <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
            <div {...contentProps} className='relative flex-1 cursor-pointer p-4 group/content focus-visible:[outline:2px_auto] transition-colors'>
              {contentChildren}
            </div>
            {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='border-t border-ws-border' />}
          </div>
        </BlockCard>
      );
    }

    return (
      <BlockPanelCard
        blockId={block.id}
        cardRef={blockRef}
        icon={<SquareMenu className='h-6 w-6' strokeWidth={2} />}
        title={t('blocks.text.title')}
        isDeleteArmed={isDeleteArmed}
        isSelected={isSelected}
        isDragOver={isDragOver}
        onDuplicate={triggerDuplicate}
        onDelete={triggerDelete}
        onDisarm={disarmDelete}
      >
        {/* The bottom radius belongs to whatever is LAST in the card — the content when there is no
            issue bar, the bar when there is — or the card renders a square corner under a rounded one. */}
        <div {...contentProps} className={`relative cursor-pointer p-4 ${issues[0] ? '' : 'rounded-b-xl'} group/content focus-visible:[outline:2px_auto] transition-colors`}>
          {contentChildren}
        </div>
        {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='rounded-b-xl' />}
      </BlockPanelCard>
    );
  }

  const usePortal = isMobile && !embedded;

  const editView = (
    <div className={usePortal
      ? 'fixed inset-x-0 top-0 z-[100] flex flex-col bg-ws-surface h-[100dvh]'
      : 'flex flex-col rounded-xl border border-ws-border bg-ws-surface md:h-full'
    }>
      <div className={`flex items-center gap-2 border-b border-ws-border bg-ws-hover px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link${!usePortal ? ' rounded-t-xl' : ''}`}>
        <SquareMenu className='h-6 w-6' strokeWidth={2} />
        <BlockNameField
          blockName={block.name ?? ''}
          titleLabel={t('blocks.text.title')}
          onBlockNameChange={onRename}
          isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
        />
        {usePortal && (
          <>
            <button
              type='button'
              onClick={phoneUndo.onUndo}
              disabled={!phoneUndo.canUndo}
              aria-label={t('common.undo')}
              className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed'
            >
              <Undo2 className='h-5 w-5' />
            </button>
            <button
              type='button'
              onClick={phoneUndo.onRedo}
              disabled={!phoneUndo.canRedo}
              aria-label={t('common.redo')}
              className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed'
            >
              <Redo2 className='h-5 w-5' />
            </button>
          </>
        )}
        <button
          onClick={handleDone}
          aria-label={t('blocks.text.closeEditor')}
          className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'
        >
          <X className='h-5 w-5' />
        </button>
      </div>
      {/* The SCROLL lives on the editor's content area, not on this container — the same shape the
          phone branch already used. When the container scrolled, the styling toolbar scrolled with
          it, so on a text block of a paragraph or more every button was off-screen exactly when a
          teacher was deepest in the text and most likely to want one. */}
      {/* LESS ROOM ABOVE THE STYLING BAR THAN BELOW IT, matching the input blocks: their toolbar
          is a `px-6 py-3` box pinned directly under the header, with a rule under it and the
          content starting `py-4` below that. The generic `p-6` here put 24px above the buttons
          and nothing between them and the text, so the bar read as floating in the panel rather
          than anchored to the top of it. The bottom half of that spacing is the bank row's
          (`pt-4` under its own rule); this supplies the top. */}
      {/*
        * EDIT OR TEST — the input blocks' own toggle, not a copy of it.
        *
        * Phone only, and only when the block HAS a test state: a plain paragraph with no bank
        * and no variables has nothing for the Test pane to show, and a toggle to an empty
        * screen is worse than no toggle. `InputBlockFrame` applies the same two conditions
        * before showing its own.
        */}
      {usePortal && hasTestState && (
        <div className='border-b border-ws-border bg-ws-surface px-4 py-2'>
          <PhoneModeToggle
            phoneMode={phoneMode}
            onPhoneModeChange={setPhoneMode}
            editModeLabel={t('blocks.phoneTabs.edit')}
            testModeLabel={t('blocks.phoneTabs.test')}
            /*
             * NOT a reroll. Switching tab is looking, not drawing — rerolling here meant a
             * random variant on every tap, so you could not flip Edit/Test to compare one
             * scenario against its text, and the Reroll button beside it had nothing left to
             * do. The input blocks use this hook only to close the feedback editor.
             *
             * It does seed the values ONCE, because the phone editor is its own instance and
             * never runs the card's roll-on-select — without it the first look at the Test pane
             * shows unresolved chips.
             */
            onSwitchToTest={() => setRolledValues(v =>
              (Object.keys(v).length > 0 ? v : rollVariableValues(variables)))}
          />
        </div>
      )}
      {usePortal && phoneMode === 'test' && hasTestState ? (
        /* The test pane: what a student would get, plus the controls to draw another. The same
           `scenarioBar` the desktop card carries — one bar, two homes. */
        <div className='flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4 pt-3'>
          {/* ABOVE the prose here too. This is a second surface, and leaving the bar under the
              content put it back where the length of the variant decided its position — the
              thing moving it on the card was meant to stop. */}
          {scenarioBar}
          <div
            className={BLOCK_TEXT_CLASS}
            // The test pane renders stored html directly, so it needs the same alias pass the
            // card above gets — otherwise this one surface shows the baked name.
            dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(remapInkColors(processVariableChipHTML(answerRefHtml(testHtml), rolledValues, chipScope))) }}
          />
        </div>
      ) : (
      <div className={usePortal
        ? 'flex flex-col gap-4 px-4 pb-4 pt-3 flex-1 overflow-hidden'
        : 'flex flex-col gap-4 px-6 pb-6 pt-3 md:flex-1 md:overflow-hidden'
      }>
        <AnchoredStylingBar toolbar={toolbar} fullScreen={usePortal} className='-mx-6 px-6 pb-3' />
        {bankRow}
        <RichTextEditor
          // Fill and scroll internally, so the toolbar stays pinned at the top of the editor while
          // the text area scrolls beneath it — the arrangement the input blocks use. The portal is
          // a full-screen sheet and so is height-bounded at every width; the inline editor's
          // container only is from `md` up, and below that it grows with the text as before.
          fill={usePortal ? true : 'md'}
          hideToolbar
          content={html}
          onChange={writeHtml}
          placeholder={t('blocks.text.placeholder')}
          // Focus on open by the same mechanism the eleven input blocks use, rather than
          // RichTextEditor's autoFocus prop, which does the same job and trips
          // jsx-a11y/no-autofocus. `isMobile` is kept and is deliberate: a text block opened on a
          // phone is full-screen and has nothing else to do, so it should be ready to type. The
          // input blocks have no equivalent because their phone editor opens on a question above a
          // whole answer setup, where stealing focus and raising the keyboard hides the rest.
          /* Registers with the shared bar above — the same wiring `BlockQuestionEditor` does
             for the eleven input blocks, inline here because that component hard-codes a
             question's field id and placeholder. */
          onEditorReady={(e) => {
            toolbar.activeEditorRef.current = e;
            toolbar.setActiveEditor(e);
            if (embedded || isMobile) e.commands.focus();
          }}
          onEditorFocus={() => {
            const e = toolbar.activeEditorRef.current;
            if (e) toolbar.setActiveEditor(e);
          }}
          editorClassName={usePortal ? 'min-h-0 flex-1 h-full overflow-y-auto' : 'min-h-[200px] md:min-h-0 md:h-full md:flex-1 md:overflow-y-auto'}
        />
        {/*
          * THE FIRST ISSUES PANEL ON A DISPLAY BLOCK, and it reports exactly one thing.
          *
          * Text has never had one because it has nothing to get wrong: there is no answer to
          * contradict and no setup to leave half-finished. A dead answer reference is the
          * exception — prose can hold one, and Text is where prose usually lives, so the most
          * common home for a reference was the one place a broken one could not be reported.
          *
          * `issues` is empty on purpose. The panel asks the context for reference problems
          * itself, so this block contributes none of its own and gains no checks it does not
          * want — the panel simply stays invisible until a reference dies.
          */}
        <IssuesPanel issues={[]} phase='editing' className='mt-2 rounded-md border border-ws-border px-3 py-2' />
      </div>
      )}
    </div>
  );

  return usePortal ? createPortal(editView, document.body) : editView;
}
